
import React, { Fragment, Suspense, useEffect } from "react"

import { Provider, useSelector } from "react-redux"
import { AuthProvider, useAuthContext } from "../features/auth/AuthProvider"
import { FeatureFlagProvider } from "../features/flags/FeatureFlagProvider"
import store, { RootState } from "../redux/store"

import { isBlogPostRoute, isBlogRoute, isEphemeralSessionRoute, isPricingRoute, isPrivacyRoute, isTemplateDetailRoute, isTemplatesRoute, isTermsRoute, POST_SIGNIN_RETURN_KEY, RouteProvider, useRouteContext } from "../features/route/RouteProvider"
import "../i18n"

import { ModalManager } from "../features/modal/ModalManager"
import { FeatureFlagOverrideToaster } from "../features/toast/FeatureFlagOverrideToaster"
import { InviteToastWatcher } from "../features/toast/InviteToastWatcher"
import { StripeSuccessToaster } from "../features/toast/StripeSuccessToaster"
import { ToastManager } from "../features/toast/ToastManager"
import { RouteHead } from "../features/route/RouteHead"
import { RouteToLatestSession } from "../features/route/RouteToLatestSession"
import { BoardFlagProvider } from "../features/flags/BoardFlagProvider"
import { BoardMemberGate } from "./boards/BoardMemberGate"
import { CookieConsentBanner } from "./CookieConsentBanner"
import ErrorBoundary from "./ErrorBoundary"
import { RetroFooter } from "./RetroFooter"

const LandingView = React.lazy(() => import("./LandingView").then(m => ({ default: m.LandingView })))
const BoardsView = React.lazy(() => import("./boards/BoardsView").then(m => ({ default: m.BoardsView })))
const SessionView = React.lazy(() => import("./session/SessionView").then(m => ({ default: m.SessionView })))
const EphemeralSessionView = React.lazy(() => import("./ephemeral/EphemeralSessionView").then(m => ({ default: m.EphemeralSessionView })))
const PrivacyPolicy = React.lazy(() => import("../features/privacy/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })))
const TermsOfService = React.lazy(() => import("../features/terms/TermsOfService").then(m => ({ default: m.TermsOfService })))
const PricingPage = React.lazy(() => import("../features/billing/PricingPage").then(m => ({ default: m.PricingPage })))
const TemplatesView = React.lazy(() => import("./templates/TemplatesView").then(m => ({ default: m.TemplatesView })))
const BlogListPage = React.lazy(() => import("./blog/BlogListPage").then(m => ({ default: m.BlogListPage })))
const BlogPostPage = React.lazy(() => import("./blog/BlogPostPage").then(m => ({ default: m.BlogPostPage })))
// NOT lazy: shared-element view transitions from /templates need the destination
// card rendered synchronously during startViewTransition's flushSync. If this
// were lazy, Suspense would render null on first click and the browser would
// have no matching template-card-* element to interpolate into.
import { TemplateDetailView } from "./templates/TemplateDetailView"

// Sets up contexts and state available throughout the app
export default function Retroboard() {
  return (
    <React.StrictMode>
      <RouteProvider>
        <AuthProvider>
          <FeatureFlagProvider>
            <Provider store={store}>
              <ErrorBoundary>
                <RootElement />
              </ErrorBoundary>
            </Provider>
          </FeatureFlagProvider>
        </AuthProvider>
      </RouteProvider>
    </React.StrictMode>
  )
}

// Starts building out the root structure of the app
function RootElement() {
  const [uid, loading, error] = useAuthContext();
  const [route, setRoute] = useRouteContext();

  const modalData = useSelector((state: RootState) => state.modal.queue[state.modal.queue.length - 1])

  // When sign-in completes, redirect to any stashed pricing destination (set by
  // anonymous CTAs on /pricing that bounced the user through sign-in first).
  useEffect(() => {
    if (!uid) return;
    let returnTo: string | null = null;
    try {
      returnTo = sessionStorage.getItem(POST_SIGNIN_RETURN_KEY);
      if (returnTo) sessionStorage.removeItem(POST_SIGNIN_RETURN_KEY);
    } catch {
      return;
    }
    if (returnTo) window.location.replace(returnTo);
  }, [uid])

  const content = (
    <Fragment>
      <RouteHead route={route} />
      <div className="rb-page-content">
        <Suspense fallback={null}>
          {isPrivacyRoute(route)
            ? <PrivacyPolicy />
            : isTermsRoute(route)
            ? <TermsOfService />
            : isPricingRoute(route)
            ? <PricingPage />
            : isTemplateDetailRoute(route)
            ? <TemplateDetailView templateId={route.templateId} from={route.from} />
            : isTemplatesRoute(route)
            ? <TemplatesView userUid={uid ?? undefined} />
            : isEphemeralSessionRoute(route)
            ? <EphemeralSessionView templateId={route.templateId} encodedState={route.encodedState} />
            : isBlogPostRoute(route)
            ? <BlogPostPage slug={route.slug} />
            : isBlogRoute(route)
            ? <BlogListPage />
            : uid
              ? route.boardUid
                ? <BoardMemberGate userUid={uid} boardUid={route.boardUid}>
                    {route.sessionUid
                      ? <SessionView />
                      : <RouteToLatestSession userUid={uid} boardUid={route.boardUid}><BoardsView userUid={uid} /></RouteToLatestSession>}
                </BoardMemberGate>
                : <BoardsView userUid={uid} />
              : <LandingView />}
        </Suspense>
      </div>
      <RetroFooter />
      {modalData && <ModalManager data={modalData} />}
      <ToastManager />
      <StripeSuccessToaster />
      <FeatureFlagOverrideToaster />
      {uid && <InviteToastWatcher userUid={uid} />}
      <CookieConsentBanner />
    </Fragment>
  )

  return route.boardUid
    ? <BoardFlagProvider boardUid={route.boardUid}>{content}</BoardFlagProvider>
    : content
}
